/**
 * Beta Cockpit — Tracy's private-beta control panel.
 *
 * One page, three cards:
 *   1. Allowlist manager — who can register during closed beta
 *   2. Friction journal — every issue you (and partner) log while battle-testing
 *   3. Reset test data — nuke YOUR OWN workspace test data (envelopes + demo seed survive)
 *
 * Route: /v3/admin/beta
 * Access: platform super admin (or any user; page just filters what they see).
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, UserPlus, Trash2, RefreshCw, ShieldCheck, ClipboardList,
  AlertTriangle, Info, KeyRound, Mail, StickyNote, RotateCcw,
  Sparkles, FileDown,
} from "lucide-react";
import { RestartTourButton } from "@/components/v3/OnboardingTour";

const SEVERITY = {
  low:     { bg: "#F1F5F9", fg: "#475569" },
  medium:  { bg: "#FEF3C7", fg: "#B45309" },
  high:    { bg: "#FFE4E6", fg: "#9F1239" },
  blocker: { bg: "#4F46E5", fg: "#FFFFFF" },
};

export default function BetaCockpitPage() {
  const [status, setStatus]     = useState(null);
  const [allowlist, setAllow]   = useState([]);
  const [journal, setJournal]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [newNote, setNewNote]   = useState("");
  const [adding, setAdding]     = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, j] = await Promise.all([
        api.get("/v3/beta/status"),
        api.get("/v3/beta/allowlist").catch(() => ({ data: { entries: [] } })),
        api.get("/v3/beta/journal?limit=200"),
      ]);
      setStatus(s.data);
      setAllow(a.data.entries || []);
      setJournal(j.data.entries || []);
    } catch {
      toast.error("Failed to load beta cockpit");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addEmail = async (e) => {
    e?.preventDefault?.();
    if (!newEmail || !newEmail.includes("@")) return toast.error("Enter a valid email");
    setAdding(true);
    try {
      await api.post("/v3/beta/allowlist", { email: newEmail.trim(), note: newNote.trim() });
      toast.success(`${newEmail} added to allowlist`);
      setNewEmail(""); setNewNote(""); await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add");
    } finally { setAdding(false); }
  };

  const removeEmail = async (email) => {
    if (!window.confirm(`Remove ${email} from the beta allowlist?`)) return;
    try {
      await api.delete(`/v3/beta/allowlist/${encodeURIComponent(email)}`);
      toast.success("Removed");
      await load();
    } catch (err) { toast.error("Failed to remove"); }
  };

  const removeEntry = async (id) => {
    try {
      await api.delete(`/v3/beta/journal/${id}`);
      await load();
    } catch { toast.error("Failed to delete"); }
  };

  const runReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/v3/beta/reset-test-data");
      toast.success(`Reset complete — ${data.total} docs cleared`);
      setResetOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setResetting(false); }
  };

  const seedDemo = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/v3/beta/seed-demo");
      const s = data.seeded || {};
      toast.success(`Seeded — ${s.properties} deals, ${s.buyers} buyer, ${s.signed_envelopes} on TC board`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Seed failed");
    } finally { setSeeding(false); }
  };

  const downloadPartnerPDF = () => {
    // Trigger a fresh download — uses the same auth cookie the browser sent for the page.
    window.open("/api/v3/beta/partner-onboarding.pdf", "_blank", "noopener");
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="beta-cockpit">
      {/* Header */}
      <div className="mb-6">
        <div className="text-[11px] font-black uppercase tracking-[0.18em] text-indigo-600 mb-1">
          Private Beta · Cockpit
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-slate-900">
          Beta Command
        </h1>
        <p className="text-slate-500 text-sm mt-1.5 max-w-2xl">
          Manage who can register, log every friction point you and your partner
          hit while running real deals, and reset your test data when you want a
          fresh run.
        </p>
      </div>

      {/* Status banner */}
      {status && (
        <div
          className={`mb-6 rounded-xl border px-4 py-3 flex items-start gap-3 ${
            status.beta_mode
              ? "border-indigo-200 bg-indigo-50 text-indigo-900"
              : "border-emerald-200 bg-emerald-50 text-emerald-900"
          }`}
          data-testid="beta-status-banner"
        >
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="text-[13px] flex-1">
            <div className="font-bold">{status.message}</div>
            <div className="text-[12px] opacity-75 mt-0.5">
              {status.beta_mode
                ? `Only allow-listed emails can register. ${status.allowlist_size} email${status.allowlist_size === 1 ? "" : "s"} in the DB allowlist (env allowlist is separate).`
                : "New signups are open to the public. Flip BETA_MODE=true to close it again."}
            </div>
          </div>
          <RestartTourButton className="shrink-0 inline-flex items-center gap-1.5 text-[11.5px] font-bold text-indigo-700 hover:text-indigo-900 bg-white/60 hover:bg-white border border-indigo-200 rounded-full px-3 py-1.5">
            Replay 60-sec tour
          </RestartTourButton>
        </div>
      )}

      {loading ? (
        <div className="h-64 grid place-items-center">
          <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Allowlist */}
          <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="beta-allowlist-card">
            <div className="flex items-center gap-2 mb-4">
              <UserPlus className="w-4 h-4 text-indigo-600" />
              <h2 className="text-base font-black text-slate-900">Invite Allowlist</h2>
              <span className="ml-auto text-[11px] text-slate-500 tabular-nums">{allowlist.length} email{allowlist.length === 1 ? "" : "s"}</span>
            </div>

            <form onSubmit={addEmail} className="flex flex-col sm:flex-row gap-2 mb-4">
              <input
                type="email"
                placeholder="partner@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-indigo-400"
                data-testid="beta-allow-email"
                required
              />
              <input
                type="text"
                placeholder="Note (optional)"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-indigo-400"
                data-testid="beta-allow-note"
              />
              <button
                type="submit"
                disabled={adding}
                className="rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm px-4 py-2 disabled:opacity-50"
                data-testid="beta-allow-submit"
              >
                {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Add"}
              </button>
            </form>

            <div className="text-[11px] text-slate-500 mb-2 inline-flex items-center gap-1.5">
              <Info className="w-3 h-3" /> The owner + env allowlist are always allowed regardless of what's below.
            </div>

            <div className="rounded-lg border border-slate-100 divide-y divide-slate-100">
              {allowlist.length === 0 ? (
                <div className="text-[12.5px] text-slate-400 italic px-3 py-6 text-center">
                  No DB allowlist entries yet. Add your partner above.
                </div>
              ) : (
                allowlist.map((e) => (
                  <div key={e.email} className="flex items-center gap-2 px-3 py-2" data-testid={`beta-allow-row-${e.email}`}>
                    <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-slate-900 truncate">{e.email}</div>
                      {e.note && <div className="text-[11px] text-slate-500 truncate">{e.note}</div>}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeEmail(e.email)}
                      className="text-slate-400 hover:text-rose-600 p-1"
                      title="Remove"
                      data-testid={`beta-allow-remove-${e.email}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </section>

          {/* Friction Journal */}
          <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="beta-journal-card">
            <div className="flex items-center gap-2 mb-4">
              <ClipboardList className="w-4 h-4 text-indigo-600" />
              <h2 className="text-base font-black text-slate-900">Friction Journal</h2>
              <span className="ml-auto text-[11px] text-slate-500 tabular-nums">{journal.length} entr{journal.length === 1 ? "y" : "ies"}</span>
            </div>

            <div className="text-[12px] text-slate-500 mb-3">
              Every time something breaks, confuses, or you wish existed — <b>tap the floating pencil</b> in the bottom-right
              of any page to log it. Entries show up here.
            </div>

            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              {journal.length === 0 ? (
                <div className="text-[12.5px] text-slate-400 italic px-3 py-8 text-center border border-dashed border-slate-200 rounded-lg">
                  No entries yet. When you run real deals, capture every rough edge here — it's your product roadmap in disguise.
                </div>
              ) : (
                journal.map((j) => {
                  const sev = SEVERITY[j.severity] || SEVERITY.medium;
                  return (
                    <div key={j.id} className="rounded-lg border border-slate-100 hover:border-indigo-200 p-2.5" data-testid={`beta-journal-row-${j.id}`}>
                      <div className="flex items-start gap-2">
                        <span
                          className="text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded"
                          style={{ backgroundColor: sev.bg, color: sev.fg }}
                        >
                          {j.severity}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] text-slate-900 leading-snug">{j.note}</div>
                          <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-2">
                            <span>{new Date(j.created_at).toLocaleString()}</span>
                            {j.url && <span>· <code className="text-slate-600">{j.url}</code></span>}
                            {j.user_email && <span>· {j.user_email}</span>}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeEntry(j.id)}
                          className="text-slate-400 hover:text-rose-600 p-1"
                          title="Delete"
                          data-testid={`beta-journal-delete-${j.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>

          {/* Partner Onboarding — seed demo + download the PDF */}
          <section className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-5 lg:col-span-2" data-testid="beta-partner-card">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-indigo-600 mt-0.5 shrink-0" />
              <div className="flex-1">
                <h2 className="text-base font-black text-slate-900 mb-1">Partner Onboarding Kit</h2>
                <p className="text-[13px] text-slate-600 mb-3 leading-relaxed">
                  Get your Acquisitions partner up and running in an afternoon.
                  Seed the workspace with 3 demo deals + a sample buyer + a
                  signed contract on the Transaction Board, then hand them the
                  4-page PDF walkthrough.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={seedDemo}
                    disabled={seeding}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm px-4 py-2 disabled:opacity-50"
                    data-testid="beta-seed-demo"
                  >
                    {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Sparkles className="w-3.5 h-3.5" /> Seed demo data</>}
                  </button>
                  <button
                    type="button"
                    onClick={downloadPartnerPDF}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-white border border-slate-200 hover:border-indigo-400 text-slate-700 font-bold text-sm px-4 py-2"
                    data-testid="beta-partner-pdf"
                  >
                    <FileDown className="w-3.5 h-3.5" /> Download onboarding PDF
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Reset Test Data */}
          <section className="rounded-2xl border-2 border-dashed border-rose-200 bg-rose-50/40 p-5 lg:col-span-2" data-testid="beta-reset-card">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" />
              <div className="flex-1">
                <h2 className="text-base font-black text-slate-900 mb-1">Reset My Test Data</h2>
                <p className="text-[13px] text-slate-600 mb-3 leading-relaxed">
                  Wipes properties, leads, buyers, tasks, notes, deal rooms, closings,
                  and journal entries from <b>your workspace only</b>. Signed DocuSign
                  envelopes, seeded demo content, and other users' data are never
                  touched. Use this when you want a clean run.
                </p>
                {!resetOpen ? (
                  <button
                    type="button"
                    onClick={() => setResetOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-white border border-rose-300 hover:border-rose-500 text-rose-700 font-bold text-sm px-4 py-2"
                    data-testid="beta-reset-open"
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Reset test data…
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={runReset}
                      disabled={resetting}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-bold text-sm px-4 py-2 disabled:opacity-50"
                      data-testid="beta-reset-confirm"
                    >
                      {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5" /> Yes, wipe it</>}
                    </button>
                    <button
                      type="button"
                      onClick={() => setResetOpen(false)}
                      className="text-sm text-slate-500 hover:text-slate-700 px-3 py-2"
                      data-testid="beta-reset-cancel"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
